import { useState, useEffect, useMemo, useCallback, memo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useRequests } from '../hooks/useRequests';
import { useRoleAccess } from '../../users/hooks/useRoleAccess';
import {
  ArrowLeft,
  Calendar,
  Clock,
  MapPin,
  Phone,
  Mail,
  AlertCircle,
  CheckCircle,
  FileText,
  User,
  Receipt,
  Eye,
} from 'lucide-react';
import type { Quote, QuoteStatus, RequestWithRelations } from '@cleanpro/common-ui';
import {
  formatCurrency,
  getQuoteStatusBadgeClasses,
  getRequestStatusBadgeClasses,
  getStatusLabel,
  getUrgencyBadgeClasses,
  DateUtils,
  AddressUtils,
  PhoneUtils
} from '@cleanpro/common-ui';

/**
 * CustomerInfoSection component displays customer information in a card layout.
 * 
 * @component
 * @param props - The component props
 * @param props.customer - The customer data from the request relation
 * @param props.request - The full request object with relations
 * @param props.navigate - Navigation function to redirect to different routes
 * 
 * @returns A memoized JSX element containing customer information including:
 * - Customer name (with fallback to "Unknown Customer")
 * - Email (with mailto link if available)
 * - Phone number (formatted, with tel link if available)
 * - Referral information (if available)
 * - "View Customer" button that navigates to customer detail page
 * 
 * @example
 * ```tsx
 * <CustomerInfoSection 
 *   customer={request.customer} 
 *   request={request} 
 *   navigate={navigate} 
 * />
 * ```
 */
const CustomerInfoSection = memo(({ customer, request, navigate }: { customer: RequestWithRelations['customer']; request: RequestWithRelations; navigate: (path: string) => void }) => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-200">
    <div className="px-6 py-4 border-b border-gray-200">
      <div className="flex items-center justify-between">

        <h3 className="text-lg font-semibold text-gray-900 flex items-center">
          <User className="h-5 w-5 mr-2" />
          Customer Information
        </h3>
        {customer?.id && (
          <button
            onClick={() => customer?.id && navigate(`/customers/${customer.id}`)}
            className="inline-flex items-center space-x-2 px-3 py-1.5 text-sm text-indigo-600 bg-indigo-100 rounded-lg hover:bg-indigo-200 transition-colors"
          >
            <Eye className="h-4 w-4" />
            <span>View Customer</span>
          </button>
        )}
      </div>
    </div>

    <div className="p-6 space-y-4">
      <div>
        <div className="text-sm font-medium text-gray-500">Name</div>
        <p className="text-gray-900 font-medium">{customer?.name ?? 'Unknown Customer'}</p>
      </div>
      {customer?.email && (
        <div>
          <div className="text-sm font-medium text-gray-500">Email</div>
          <div className="flex items-center">
            <Mail className="h-4 w-4 text-gray-400 mr-2" />
            <a href={`mailto:${customer.email}`} className="text-indigo-600 hover:text-indigo-700">
              {customer.email}
            </a>
          </div>
        </div>
      )}
      {customer?.phone && (
        <div>
          <div className="text-sm font-medium text-gray-500">Phone</div>
          <div className="flex items-center">
            <Phone className="h-4 w-4 text-gray-400 mr-2" />
            <a href={`tel:${customer.phone}`} className="text-indigo-600 hover:text-indigo-700">
              {PhoneUtils.format(customer.phone)}
            </a>
          </div>
        </div>
      )}

      {request?.referral && (
        <div>
          <div className="text-sm font-medium text-gray-500">Referral</div>
          <div className="flex items-center">
            {request.referral}
          </div>
        </div>
      )}
    </div>
  </div>
));

CustomerInfoSection.displayName = 'CustomerInfoSection';

const ServiceDetailsSection = memo(({ request, navigate }: { request: RequestWithRelations; navigate: (path: string) => void }) => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-200">
    <div className="px-6 py-4 border-b border-gray-200">
      <div className="flex items-center justify-between">

        <h3 className="text-lg font-semibold text-gray-900 flex items-center">
          <Eye className="h-5 w-5 mr-2" />
          Service Details
        </h3>
        {request.service?.id && (
          <button
            onClick={() => request.service?.id && navigate(`/services/${request.service.id}`)}
            className="inline-flex items-center space-x-2 px-3 py-1.5 text-sm text-indigo-600 bg-indigo-100 rounded-lg hover:bg-indigo-200 transition-colors"
          >
            <Eye className="h-4 w-4" />
            <span>View Service</span>
          </button>
        )}
      </div>
    </div>

    <div className="p-6 space-y-4">
      <div>
        <div className="text-sm font-medium text-gray-500">Service Type</div>
        {request.service?.id ? (
          <button
            onClick={() => request.service?.id && navigate(`/services/${request.service.id}`)}
            className="text-indigo-600 hover:text-indigo-700 font-medium text-left"
          >
            {request.service.name ?? 'Unknown Service'}
          </button>
        ) : (
          <p className="text-gray-900 font-medium">{request.service?.name ?? 'Unknown Service'}</p>
        )}
      </div>

      {request.requestedDateTime && (
        <div>
          <div className="text-sm font-medium text-gray-500">Requested Date</div>
          <div className="flex items-center">
            <Calendar className="h-4 w-4 text-gray-400 mr-2" />
            <p className="text-gray-900">
              {DateUtils.formatDateLong(request.requestedDateTime)}</p>
          </div>
        </div>
      )}

      {request.requestedDateTime && (
        <div>
          <div className="text-sm font-medium text-gray-500">Preferred Time</div>
          <div className="flex items-center">
            <Clock className="h-4 w-4 text-gray-400 mr-2" />
            <p className="text-gray-900">
              {DateUtils.formatTimeFromDate(request.requestedDateTime)}
            </p>
          </div>
        </div>
      )}

      {!!request.estimatedDuration && (
        <div>
          <div className="text-sm font-medium text-gray-500">Estimated Duration</div>
          <p className="text-gray-900">{DateUtils.formatDurationByMinutes(request.estimatedDuration)}</p>
        </div>
      )}

      {request.lawnAreaSqft && (
        <div>
          <div className="text-sm font-medium text-gray-500">Lawn Area</div>
          <p className="text-gray-900">{request.lawnAreaSqft.toLocaleString()} sq ft</p>
        </div>
      )}

      {request.notes && (
        <div>
          <div className="text-sm font-medium text-gray-500">Customer Notes</div>
          <p className="text-gray-900 whitespace-pre-wrap">{request.notes}</p>
        </div>
      )}

      {request.accessInstructions && (
        <div>
          <div className="text-sm font-medium text-gray-500">Access Instructions</div>
          <p className="text-gray-900 whitespace-pre-wrap">{request.accessInstructions}</p>
        </div>
      )}
    </div>
  </div>
));

ServiceDetailsSection.displayName = 'ServiceDetailsSection';

const PropertyInfoSection = memo(({ property, navigate }: { property: RequestWithRelations['property']; navigate: (path: string) => void }) => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-200">
    <div className="px-6 py-4 border-b border-gray-200">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center">
          <MapPin className="h-5 w-5 mr-2" />
          Property Information
        </h3>
        {property?.id && (
          <button
            onClick={() => navigate(`/properties/${property.id}`)}
            className="inline-flex items-center space-x-2 px-3 py-1.5 text-sm text-indigo-600 bg-indigo-100 rounded-lg hover:bg-indigo-200 transition-colors"
          >
            <Eye className="h-4 w-4" />
            <span>View Property</span>
          </button>
        )}
      </div>
    </div>
    <div className="p-6">
      <div>
        <div className="text-sm font-medium text-gray-500">Service Address</div>
        <div className="flex items-start mt-1">
          <MapPin className="h-4 w-4 text-gray-400 mr-2 mt-0.5 flex-shrink-0" />
          <div className="text-gray-900">
            {(() => {
              const addressLines = 
                AddressUtils.formatAddressLines(
                  property?.addressLine1 + ' ' + 
                  property?.addressLine2 + ', ' +
                  property?.city + ', ' +
                  property?.state + ' ' +
                  property?.postalCode
                );
              if (!addressLines) return <span className="text-gray-400 italic">No address</span>;
              return addressLines.map((line) => (
                <div key={line} className="truncate">{line}</div>
              ));
            })()}
          </div>
        </div>
      </div>

      {property?.propertyType && (
        <div className="mt-4">
          <div className="text-sm font-medium text-gray-500">Property Type</div>
          <p className="text-gray-900 capitalize">{property.propertyType.replace('_', ' ')}</p>
        </div>
      )}

      {property?.accessInstructions && (
        <div className="mt-4">
          <div className="text-sm font-medium text-gray-500">Access Instructions</div>
          <p className="text-gray-900 whitespace-pre-wrap">{property.accessInstructions}</p>
        </div>
      )}

      {property?.specialInstructions && (
        <div className="mt-4">
          <div className="text-sm font-medium text-gray-500">Special Instructions</div>
          <p className="text-gray-900 whitespace-pre-wrap">{JSON.stringify(property.specialInstructions, null, 2)}</p>
        </div>
      )}
    </div>
  </div>
));

PropertyInfoSection.displayName = 'PropertyInfoSection';

const QuotesList = memo(({ 
  quotes, 
  navigate, 
  onGenerateQuote,
  canGenerate
}: { 
  quotes: Partial<Quote>[]; 
  navigate: (path: string) => void;
  onGenerateQuote: () => void;
  canGenerate: boolean;
}) => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-200">
    <div className="px-6 py-4 border-b border-gray-200">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center">
          <Receipt className="h-5 w-5 mr-2" />
          Quotes ({quotes.length})
        </h3>
      </div>
    </div>
    <div className="p-6">
      {quotes.length === 0 ? (
        <div className="text-center py-8">
          <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h4 className="text-lg font-medium text-gray-900 mb-2">No Quotes Yet</h4>
          <p className="text-gray-500 mb-4">
            This request doesn't have any quotes yet. Generate a quote to provide pricing for the customer.
          </p>
          {canGenerate && (
            <button
              onClick={onGenerateQuote}
              className="inline-flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <FileText className="h-4 w-4" />
              <span>Generate Quote</span>
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {quotes.map((quote, index) => {
            const resolvedStatus = quote.status ?? 'draft';
            return (
              <div
                key={quote.id}
                className={`relative border rounded-lg p-4 transition-all hover:shadow-md ${
                  index === 0
                    ? 'border-blue-200 bg-blue-50 ring-2 ring-blue-100'
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
              >
                {index === 0 && (
                  <div className="absolute -top-2 left-4">
                    <span className="inline-flex px-2 py-1 text-xs font-medium bg-blue-600 text-white rounded">
                      Latest Quote
                    </span>
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div>
                      <div className="flex items-center space-x-2">
                        <h4 className="font-medium text-gray-900">{quote.quoteNumber}</h4>
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-medium rounded border ${getQuoteStatusBadgeClasses(resolvedStatus)}`}
                        >
                          {getStatusLabel(resolvedStatus, 'quote')}
                        </span>
                      </div>
                      <p className="text-sm text-gray-500 mt-1">
                        {quote.createdAt && `Created ${DateUtils.formatDateLong(quote.createdAt)}`}
                        {quote.validUntil && (
                          <span> • Valid until {DateUtils.formatDateLong(quote.validUntil)}</span>
                        )}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="text-right">
                      <div className="flex items-center text-lg font-semibold text-gray-900">
                        {formatCurrency(quote.totalAmount ?? 0)}
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => navigate(`/quotes/${quote.id}`)}
                        className="inline-flex items-center space-x-1 px-3 py-1.5 text-sm text-blue-600 bg-blue-100 rounded-lg hover:bg-blue-200 transition-colors"
                      >
                        <Eye className="h-4 w-4" />
                        <span>View</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  </div>
));

QuotesList.displayName = 'QuotesList';

export default function RequestDetail() {
  const { requestId } = useParams<{ requestId: string }>();
  const navigate = useNavigate();
  const permissions = useRoleAccess();
  
  const [request, setRequest] = useState<RequestWithRelations | null>(null);
  const [quotes, setQuotes] = useState<Partial<Quote>[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [updating, setUpdating] = useState(false);

  const {
    getRequestById,
    updateRequestStatus,
    requests,
    loading: isRequestsLoading,
    error: requestsError,
  } = useRequests();

  useEffect(() => {
    const fetchRequest = async () => {
      if (!requestId) {
        setError('Request ID is required');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const cached = requests.find((item) => item.id === requestId) ?? null;
        if (cached) {
          setRequest(cached);
        }

        const resolved = await getRequestById(requestId);
        if (resolved) {
          setRequest(resolved);
        }

        const { supabase } = await import('@cleanpro/common-ui');
        const { data: quotesData, error: quotesError } = await supabase
          .from('quotes')
          .select(`
            id,
            quote_number,
            total_amount,
            status,
            created_at,
            valid_until
          `)
          .eq('request_id', requestId)
          .order('created_at', { ascending: false });

        if (quotesError) {
          console.error('Error fetching quotes:', quotesError);
        } else {
          const transformedQuotes: Partial<Quote>[] = (quotesData || []).map(quote => ({
            id: quote.id,
            quoteNumber: quote.quote_number || 'N/A',
            totalAmount: quote.total_amount,
            status: (quote.status || 'draft') as QuoteStatus,
            validUntil: quote.valid_until
          }));
          setQuotes(transformedQuotes);
        }
      } catch (err) {
        console.error('Error fetching request:', err);
        setError('Failed to load request details');
      } finally {
        setLoading(false);
      }
    };

    fetchRequest();
  }, [getRequestById, requestId, requests]);

  const latestRequestState = useMemo(() => {
    if (!requestId) return null;
    return requests.find((item) => item.id === requestId) ?? request;
  }, [requestId, request, requests]);

  const handleStatusUpdate = useCallback(async (newStatus: RequestWithRelations['status']) => {
    if (!request || !requestId) return;

    try {
      setUpdating(true);
      await updateRequestStatus(requestId, newStatus);
      setRequest({ ...request, status: newStatus });
    } catch (_err) {
      console.error('Failed to update status:', _err);
    } finally {
      setUpdating(false);
    }
  }, [request, requestId, updateRequestStatus]);

  const handleGenerateQuote = useCallback(() => {
    navigate(`/quotes/create?requestId=${requestId}`);
  }, [navigate, requestId]);

  const handleBack = useCallback(() => {
    navigate('/requests');
  }, [navigate]);

  if (loading || isRequestsLoading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-gray-500">Loading request details...</div>
      </div>
    );
  }

  if (error || requestsError || !latestRequestState) {
    return (
      <div className="space-y-6">
        <div className="flex items-center">
          <button
            onClick={handleBack}
            className="flex items-center text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Requests
          </button>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <AlertCircle className="h-12 w-12 text-red-600 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-red-800 mb-2">Request Not Found</h3>
          <p className="text-red-700">{error ?? requestsError}</p>
        </div>
      </div>
    );
  }

  const activeRequest = latestRequestState;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={handleBack}
            className="flex items-center text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Requests
          </button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Request Details</h1>
            <p className="text-gray-600 mt-1">Service request from {activeRequest.customer?.name ?? 'Unknown Customer'}</p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <span
            className={`inline-flex px-3 py-1 text-sm font-medium rounded-lg border ${getRequestStatusBadgeClasses(activeRequest.status)}`}
          >
            {getStatusLabel(activeRequest.status, 'request')}
          </span>
          <span
            className={`inline-flex px-3 py-1 text-sm font-medium rounded-lg border ${getUrgencyBadgeClasses(activeRequest.urgency)}`}
          >
            {activeRequest.urgency.charAt(0).toUpperCase() + activeRequest.urgency.slice(1)} Priority
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          
          {/* Service Details */}
          <ServiceDetailsSection request={activeRequest} navigate={navigate} />

          {/* Property Information */}
          <PropertyInfoSection property={activeRequest.property} navigate={navigate} />

          {/* Customer Information */}
          <CustomerInfoSection customer={activeRequest.customer} request={activeRequest} navigate={navigate} />

          {/* Quotes Section */}
          <QuotesList 
            quotes={quotes} 
            navigate={navigate}
            onGenerateQuote={handleGenerateQuote}
            canGenerate={permissions.canEditJobs && (activeRequest.status === 'assessed' || activeRequest.status === 'pending')}
          />
        </div>

        {/* Actions Sidebar */}
        <div className="space-y-6">
          {/* Status Actions */}
          {permissions.canEditJobs && activeRequest && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">Actions</h3>
              </div>
              <div className="p-6 space-y-3">
                {activeRequest.status === 'pending' && (
                  <button
                    onClick={() => handleStatusUpdate('assessed')}
                    disabled={updating}
                    className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
                  >
                    <CheckCircle className="h-4 w-4" />
                    <span>{updating ? 'Updating...' : 'Mark as Assessed'}</span>
                  </button>
                )}
                
                {(activeRequest.status === 'assessed' || activeRequest.status === 'pending') && quotes.length === 0 && (
                  <button
                    onClick={handleGenerateQuote}
                    className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    <FileText className="h-4 w-4" />
                    <span>Generate Quote</span>
                  </button>
                )}

                {activeRequest.status !== 'cancelled' && (
                  <button
                    onClick={() => handleStatusUpdate('cancelled')}
                    disabled={updating}
                    className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
                  >
                    <span>{updating ? 'Updating...' : 'Cancel Request'}</span>
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Request Timeline */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Timeline</h3>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <div className="text-sm font-medium text-gray-500">Created</div>
                <p className="text-gray-900 text-sm">
                  {DateUtils.formatDateLong(activeRequest.createdAt)} at{' '}
                  {DateUtils.formatTimeFromDate(activeRequest.createdAt)}
                </p>
              </div>
              <div>
                <div className="text-sm font-medium text-gray-500">Last Updated</div>
                <p className="text-gray-900 text-sm">
                  {DateUtils.formatDateLong(activeRequest.updatedAt)} at{' '}
                  {DateUtils.formatTimeFromDate(activeRequest.updatedAt)}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
